import { useEffect } from "react";
import { Control, FieldPath, UseFormSetValue, useWatch } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ParkingRateFormValues, TICKET_PROVIDER_OPTIONS } from "../parking-payment.const";
import { TicketProviderEnum } from "../payment-zone-area.type";
import { useGetCityConfig } from "../hooks/use-city-config";
import { EditableStringList } from "./city-config/editable-string-list";
import { SectionDivider } from "./section-divider";

const NONE = "__none__";

type ProviderParkingSectionProps = {
  control: Control<ParkingRateFormValues>;
  setValue: UseFormSetValue<ParkingRateFormValues>;
  cityId: string;
};

type SourcedSelectProps = {
  control: Control<ParkingRateFormValues>;
  name: FieldPath<ParkingRateFormValues>;
  label: string;
  options: string[];
  required?: boolean;
};

const SourcedSelect = ({ control, name, label, options, required }: SourcedSelectProps) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FormLabel>
          {label}
          {required && <span className="text-red-500"> *</span>}
        </FormLabel>
        <Select
          onValueChange={(v) => field.onChange(v === NONE ? undefined : v)}
          value={(field.value as string | undefined) ?? ""}
          disabled={options.length === 0}
        >
          <FormControl>
            <SelectTrigger>
              <SelectValue
                placeholder={options.length ? "Sélectionnez une valeur" : "Aucune valeur configurée pour cette ville"}
              />
            </SelectTrigger>
          </FormControl>
          <SelectContent>
            {!required && <SelectItem value={NONE}>Aucun</SelectItem>}
            {options.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FormMessage />
      </FormItem>
    )}
  />
);

const FreeTextField = ({
  control,
  name,
  label,
  placeholder,
  required,
}: {
  control: Control<ParkingRateFormValues>;
  name: FieldPath<ParkingRateFormValues>;
  label: string;
  placeholder: string;
  required?: boolean;
}) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FormLabel>
          {label}
          {required && <span className="text-red-500"> *</span>}
        </FormLabel>
        <FormControl>
          <Input placeholder={placeholder} {...field} value={(field.value as string | undefined) ?? ""} />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

export const ProviderParkingSection = ({ control, setValue, cityId }: ProviderParkingSectionProps) => {
  const { data, isLoading, error } = useGetCityConfig(cityId);
  const ticketProvider = data?.ticketProvider;
  const provider = ticketProvider?.provider;
  const currentProvider = useWatch({ control, name: "providerParking.provider" });
  const currentAreaNo = useWatch({ control, name: "providerParking.easyparkAreaNo" });

  // Provider is locked to the city's; reset stale fields when it differs from what the rate stored.
  // setValue merges keys, so requiresRight (provider-agnostic) must be cleared explicitly.
  useEffect(() => {
    if (isLoading || error) return;
    if (provider) {
      if (currentProvider !== provider) setValue("providerParking", { provider, requiresRight: undefined });
    } else if (currentProvider) {
      setValue("providerParking", { provider: undefined, requiresRight: undefined });
    }
  }, [provider, currentProvider, isLoading, error, setValue]);

  // A stored area may have been removed from the city's list since the rate was saved.
  useEffect(() => {
    if (isLoading || error || provider !== TicketProviderEnum.Easypark) return;
    const areas = ticketProvider?.easyparkAreas ?? [];
    if (currentAreaNo != null && !areas.some((area) => area.areaNo === currentAreaNo)) {
      setValue("providerParking.easyparkAreaNo", undefined);
    }
  }, [provider, ticketProvider, currentAreaNo, isLoading, error, setValue]);

  if (isLoading) {
    return <p className="py-4 text-sm text-muted-foreground">Chargement de la configuration de la ville…</p>;
  }

  if (error) {
    return <p className="py-4 text-sm text-destructive">Impossible de charger la configuration de la ville.</p>;
  }

  if (!provider) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
        <p className="font-medium text-amber-700 dark:text-amber-300">Aucun fournisseur configuré pour cette ville.</p>
        <p className="text-amber-600/80 dark:text-amber-400/70">
          Configurez un concentrateur de tickets dans la configuration de la ville pour permettre le paiement in-house.
        </p>
      </div>
    );
  }

  const providerLabel = TICKET_PROVIDER_OPTIONS.find((opt) => opt.value === provider)?.label ?? provider;

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Ces identifiants relient la tarification au moteur de billetterie du fournisseur. Ils sont optionnels — la
        tarification peut être enregistrée sans — mais la ville ne peut pas passer en billetterie in-house tant qu'ils
        ne sont pas renseignés.
      </p>

      <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
        Fournisseur : <span className="font-medium">{providerLabel}</span>{" "}
        <span className="text-muted-foreground">(défini par la ville)</span>
      </div>

      {provider === TicketProviderEnum.Easypark && (
        <FormField
          control={control}
          name="providerParking.easyparkAreaNo"
          render={({ field }) => {
            const areas = ticketProvider.easyparkAreas ?? [];
            return (
              <FormItem>
                <FormLabel>
                  Zone EasyPark <span className="text-red-500">*</span>
                </FormLabel>
                <Select
                  onValueChange={(v) => field.onChange(Number(v))}
                  value={field.value != null ? String(field.value) : ""}
                  disabled={areas.length === 0}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={areas.length ? "Sélectionnez une zone" : "Aucune zone configurée pour cette ville"}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {areas.map((area) => (
                      <SelectItem key={area.areaNo} value={String(area.areaNo)}>
                        {area.name} — {area.areaNo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      )}

      {(provider === TicketProviderEnum.Extenso || provider === TicketProviderEnum.ExtensoE2) && (
        <>
          <SourcedSelect
            control={control}
            name="providerParking.extensoValidityArea"
            label="Zone de validité"
            options={(ticketProvider.extensoValidityAreas ?? []).map((area) => area.name)}
            required
          />
          <SourcedSelect
            control={control}
            name="providerParking.extensoProfile"
            label="Profil"
            options={ticketProvider.extensoProfiles ?? []}
          />
        </>
      )}

      {provider === TicketProviderEnum.Polyconseil && (
        <>
          <SourcedSelect
            control={control}
            name="providerParking.polyconseilZone"
            label="Zone"
            options={ticketProvider.polyconseilZones ?? []}
            required
          />
          <SourcedSelect
            control={control}
            name="providerParking.polyconseilTariffCode"
            label="Code tarifaire"
            options={ticketProvider.polyconseilTariffCodes ?? []}
            required
          />
          <SourcedSelect
            control={control}
            name="providerParking.polyconseilPricingCategory"
            label="Catégorie tarifaire"
            options={ticketProvider.polyconseilPricingCategories ?? []}
            required
          />
        </>
      )}

      {provider === TicketProviderEnum.PrestoPlatform && (
        <>
          <FreeTextField
            control={control}
            name="providerParking.prestoPlatformUrbanParkingSiteId"
            label="Identifiant du site de stationnement"
            placeholder="Ex. SITE-9"
            required
          />
          <FreeTextField
            control={control}
            name="providerParking.prestoPlatformTariffType"
            label="Type de tarif"
            placeholder="Ex. STD"
          />
          <SourcedSelect
            control={control}
            name="providerParking.prestoPlatformPointOfSale"
            label="Point de vente"
            options={ticketProvider.prestoPlatformPointsOfSale ?? []}
          />
          <FormField
            control={control}
            name="providerParking.prestoPlatformParkingPermits"
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormLabel>Permis de stationnement</FormLabel>
                <FormControl>
                  <EditableStringList value={(value as string[]) ?? []} onChange={onChange} placeholder="Ex. P1" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}

      <SectionDivider label="Accès au tarif" />

      <FormField
        control={control}
        name="providerParking.requiresRight"
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <div className="flex flex-row items-center space-x-3">
              <FormControl>
                <Checkbox checked={!!value} onCheckedChange={onChange} />
              </FormControl>
              <FormLabel className="font-normal">Nécessite un droit de stationnement</FormLabel>
            </div>
            <p className="text-xs text-muted-foreground">
              Coché, seules les plaques disposant d'un droit valide (résident, commerçant, permis…) correspondant au
              profil et à la zone de ce tarif peuvent le sélectionner et payer ; les autres sont refusées. Décoché, le
              tarif est ouvert à tous. Un tarif correspond à un seul profil : créez un tarif distinct par profil.
            </p>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
